import React, { useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { theme } from '../../styles/theme';
import { getUserProfile, UserProfile } from '../../services/userProfileService';

const Profile: React.FC = () => {
    const history = useHistory();
    const [user, setUser] = useState<UserProfile | null>(null);
    const [unlockedCount, setUnlockedCount] = useState(0);

    useEffect(() => {
        const profile = getUserProfile();
        setUser(profile);
        setUnlockedCount(profile.achievements.filter(a => a.unlocked).length);
    }, []);

    return (
        <div style={{
            padding: '20px',
            maxWidth: '600px',
            margin: '0 auto',
            paddingBottom: '100px',
        }}>
            {!user ? (
                <div style={{ textAlign: 'center', padding: '40px 20px' }}>Loading profile...</div>
            ) : (
                <>
                    {/* Profile Header */}
                    <div style={{
                        textAlign: 'center',
                        marginBottom: '32px',
                    }}>
                        <div style={{
                            width: '100px',
                            height: '100px',
                            borderRadius: '50%',
                            backgroundColor: theme.colors.accent,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontSize: '48px',
                            margin: '0 auto 16px',
                            boxShadow: theme.shadows.lg,
                        }}>
                            🧩
                        </div>

                        <h1 style={{
                            fontSize: '24px',
                            fontWeight: 700,
                            color: theme.colors.textPrimary,
                            marginBottom: '4px',
                        }}>
                            {user.name}
                        </h1>
                        <p style={{
                            color: theme.colors.textSecondary,
                            fontSize: '14px',
                        }}>
                            {user.nameRu}
                        </p>
                    </div>

                    {/* Level Progress */}
                    <div style={{
                        padding: '20px',
                        backgroundColor: theme.colors.cardBg,
                        borderRadius: '16px',
                        marginBottom: '24px',
                        boxShadow: theme.shadows.sm,
                    }}>
                        <div style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            marginBottom: '12px',
                        }}>
                            <span style={{
                                fontSize: '18px',
                                fontWeight: 600,
                                color: theme.colors.textPrimary,
                            }}>
                                Level {user.level}
                            </span>
                            <span style={{
                                fontSize: '14px',
                                color: theme.colors.textSecondary,
                            }}>
                                {user.xp % 1000} / 1000 XP
                            </span>
                        </div>

                        <div style={{
                            width: '100%',
                            height: '12px',
                            backgroundColor: theme.colors.cardBorder,
                            borderRadius: '6px',
                            overflow: 'hidden',
                        }}>
                            <div style={{
                                width: `${(user.xp % 1000) / 10}%`,
                                height: '100%',
                                backgroundColor: theme.colors.accent,
                                borderRadius: '6px',
                            }} />
                        </div>
                    </div>

                    {/* Stats Grid */}
                    <div style={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(2, 1fr)',
                        gap: '12px',
                        marginBottom: '32px',
                    }}>
                        <div style={{
                            padding: '20px',
                            backgroundColor: theme.colors.cardBg,
                            borderRadius: '16px',
                            textAlign: 'center',
                            boxShadow: theme.shadows.sm,
                        }}>
                            <div style={{ fontSize: '32px', marginBottom: '8px' }}>🧩</div>
                            <div style={{
                                fontSize: '24px',
                                fontWeight: 700,
                                color: theme.colors.textPrimary,
                            }}>
                                {user.stats.puzzlesCompleted}
                            </div>
                            <div style={{
                                fontSize: '12px',
                                color: theme.colors.textSecondary,
                            }}>
                                Puzzles Completed
                            </div>
                        </div>

                        <div style={{
                            padding: '20px',
                            backgroundColor: theme.colors.cardBg,
                            borderRadius: '16px',
                            textAlign: 'center',
                            boxShadow: theme.shadows.sm,
                        }}>
                            <div style={{ fontSize: '32px', marginBottom: '8px' }}>🔥</div>
                            <div style={{
                                fontSize: '24px',
                                fontWeight: 700,
                                color: theme.colors.textPrimary,
                            }}>
                                {user.stats.streakDays}
                            </div>
                            <div style={{
                                fontSize: '12px',
                                color: theme.colors.textSecondary,
                            }}>
                                Day Streak
                            </div>
                        </div>

                        <div style={{
                            padding: '20px',
                            backgroundColor: theme.colors.cardBg,
                            borderRadius: '16px',
                            textAlign: 'center',
                            boxShadow: theme.shadows.sm,
                        }}>
                            <div style={{ fontSize: '32px', marginBottom: '8px' }}>🪙</div>
                            <div style={{
                                fontSize: '24px',
                                fontWeight: 700,
                                color: '#FFD700',
                            }}>
                                {user.coins}
                            </div>
                            <div style={{
                                fontSize: '12px',
                                color: theme.colors.textSecondary,
                            }}>
                                Coins Earned
                            </div>
                        </div>

                        <div style={{
                            padding: '20px',
                            backgroundColor: theme.colors.cardBg,
                            borderRadius: '16px',
                            textAlign: 'center',
                            boxShadow: theme.shadows.sm,
                        }}>
                            <div style={{ fontSize: '32px', marginBottom: '8px' }}>⭐</div>
                            <div style={{
                                fontSize: '24px',
                                fontWeight: 700,
                                color: theme.colors.textPrimary,
                            }}>
                                {unlockedCount}
                            </div>
                            <div style={{
                                fontSize: '12px',
                                color: theme.colors.textSecondary,
                            }}>
                                Achievements
                            </div>
                        </div>
                    </div>

                    {/* Achievements */}
                    <div style={{ marginBottom: '32px' }}>
                        <h2 style={{
                            fontSize: '18px',
                            fontWeight: 600,
                            color: theme.colors.textPrimary,
                            marginBottom: '16px',
                        }}>
                            🏆 Achievements / Достижения
                        </h2>

                        <div style={{
                            display: 'grid',
                            gridTemplateColumns: 'repeat(3, 1fr)',
                            gap: '12px',
                        }}>
                            {user.achievements.map(achievement => (
                                <div
                                    key={achievement.id}
                                    style={{
                                        padding: '16px 8px',
                                        backgroundColor: achievement.unlocked ? theme.colors.cardBg : theme.colors.bgPrimary,
                                        borderRadius: '12px',
                                        textAlign: 'center',
                                        opacity: achievement.unlocked ? 1 : 0.5,
                                        border: `1px solid ${theme.colors.cardBorder}`,
                                    }}
                                >
                                    <div style={{
                                        fontSize: '28px',
                                        marginBottom: '8px',
                                        filter: achievement.unlocked ? 'none' : 'grayscale(100%)',
                                    }}>
                                        {achievement.icon}
                                    </div>
                                    <div style={{
                                        fontSize: '11px',
                                        fontWeight: 500,
                                        color: theme.colors.textPrimary,
                                    }}>
                                        {achievement.title}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* Play Button */}
                    <button
                        onClick={() => history.push('/')}
                        style={{
                            width: '100%',
                            padding: '16px',
                            backgroundColor: theme.colors.accent,
                            color: '#fff',
                            border: 'none',
                            borderRadius: '12px',
                            fontSize: '16px',
                            fontWeight: 600,
                            cursor: 'pointer',
                            boxShadow: theme.shadows.md,
                        }}
                    >
                        Keep Playing! 🧩
                    </button>
                </>
            )}
        </div>
    );
};

export default Profile;
